import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  Typography,
  Row,
  Col,
  Progress,
  Badge,
  Space,
  message,
  Divider,
  Tag,
  Timeline,
  Select,
  DatePicker,
  Tabs,
} from "antd";
import {
  DatabaseOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  FileTextOutlined,
  TrophyOutlined,
  SafetyOutlined,
  BankOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UserOutlined,
  TeamOutlined,
  DashboardOutlined,
  DollarOutlined,
  CloudOutlined,
  MonitorOutlined,
  BellOutlined,
  RocketOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import "./AdminDashboard.css";
import TwoFactorAuthPopup from "../TwoFactorAuthPopup";
import { ThemeContext } from "../../context/ThemeContext";


const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useContext(ThemeContext);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState("7d");
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [userStatsLoading, setUserStatsLoading] = useState(false);
  const [show2FAPopup, setShow2FAPopup] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [twoFAMethods, setTwoFAMethods] = useState([]);
  const [twoFAHandled, setTwoFAHandled] = useState(false);
  const [activeTab, setActiveTab] = useState(0);



  // Enhanced stats with more metrics
  const [stats, setStats] = useState({
    totalOperations: 32,
    completedOperations: 28,
    pendingOperations: 3,
    failedOperations: 1,
    totalUsers: 1247,
    activeUsers: 892,
    totalRevenue: 45678,
    monthlyGrowth: 12.5,
    systemUptime: 99.8,
    avgResponseTime: 245,
    dataProcessed: 2.4,
    alertsCount: 3,
  });

  // Enhanced recent activity with more details
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemStatus, setSystemStatus] = useState("healthy");
  const [performanceMetrics, setPerformanceMetrics] = useState({
    cpuUsage: 45,
    memoryUsage: 62,
    diskUsage: 78,
    networkUsage: 23,
  });

  const [deactivatedUsersCount, setDeactivatedUsersCount] = useState(0);
  const [deletedUsersCount, setDeletedUsersCount] = useState(0);

  // User stats state
  const [userStats, setUserStats] = useState({
    pendingActionUsers: 0,
    deactivatedUsers: 0,
    totalUsers: 0,
    activeUsers: 0,
    rejectedActions: 0,
    deletedUsers: 0
  });

  // Enhanced data operations
  const [dataOperations, setDataOperations] = useState([
    {
      category: "Data Fetching",
      icon: <DownloadOutlined />,
      color: "#1890ff",
      description: "External data integration and synchronization",
      operations: [
        {
          name: "Care Rating",
          key: "care",
          status: "idle",
          lastRun: "2 hours ago",
          successRate: 98,
        },
        {
          name: "Crisil Rating",
          key: "crisil",
          status: "idle",
          lastRun: "1 hour ago",
          successRate: 95,
        },
        {
          name: "Fitch Rating",
          key: "fitch",
          status: "idle",
          lastRun: "3 hours ago",
          successRate: 92,
        },
        {
          name: "ICRA Rating",
          key: "icra",
          status: "idle",
          lastRun: "4 hours ago",
          successRate: 97,
        },
        {
          name: "India Rating",
          key: "india",
          status: "idle",
          lastRun: "1 hour ago",
          successRate: 94,
        },
      ],
    },
    {
      category: "News Management",
      icon: <FileTextOutlined />,
      color: "#52c41a",
      description: "News aggregation and content management",
      operations: [
        {
          name: "Economic Times",
          key: "news_et",
          status: "idle",
          lastRun: "30 min ago",
          successRate: 99,
        },
        {
          name: "Business Standard",
          key: "news_bs",
          status: "idle",
          lastRun: "45 min ago",
          successRate: 96,
        },
        {
          name: "Financial Express",
          key: "news_fe",
          status: "idle",
          lastRun: "1 hour ago",
          successRate: 93,
        },
        {
          name: "Livemint",
          key: "news_lm",
          status: "idle",
          lastRun: "2 hours ago",
          successRate: 98,
        },
        {
          name: "Moneycontrol",
          key: "news_mc",
          status: "idle",
          lastRun: "1 hour ago",
          successRate: 95,
        },
      ],
    },
    {
      category: "Market Data",
      icon: <BarChartOutlined />,
      color: "#722ed1",
      description: "Real-time market data and analytics",
      operations: [
        {
          name: "NSE Debt ISIN List",
          key: "isins",
          status: "idle",
          lastRun: "15 min ago",
          successRate: 100,
        },
        {
          name: "NSE Debt Announcement",
          key: "debt-announcement",
          status: "idle",
          lastRun: "20 min ago",
          successRate: 99,
        },
        {
          name: "NSE Bonds Traded",
          key: "capital-volume",
          status: "idle",
          lastRun: "10 min ago",
          successRate: 100,
        },
        {
          name: "BSE Corporate Bonds",
          key: "bse-corporate-bonds",
          status: "idle",
          lastRun: "25 min ago",
          successRate: 98,
        },
        {
          name: "Trade Repository",
          key: "trade-repository",
          status: "idle",
          lastRun: "5 min ago",
          successRate: 100,
        },
      ],
    },
    {
      category: "Financial Data",
      icon: <BankOutlined />,
      color: "#fa8c16",
      description: "Financial analysis and reporting",
      operations: [
        {
          name: "Financial Results",
          key: "financial",
          status: "idle",
          lastRun: "1 hour ago",
          successRate: 97,
        },
        {
          name: "Credit Notes & Cash Flow",
          key: "credit",
          status: "idle",
          lastRun: "2 hours ago",
          successRate: 94,
        },
        {
          name: "Yield to Maturity",
          key: "ytm",
          status: "idle",
          lastRun: "30 min ago",
          successRate: 99,
        },
        {
          name: "Credit Ratings",
          key: "credit-ratings",
          status: "idle",
          lastRun: "1 hour ago",
          successRate: 96,
        },
        {
          name: "Termsheet Universe",
          key: "termsheet-universe",
          status: "idle",
          lastRun: "45 min ago",
          successRate: 95,
        },
      ],
    },
    {
      category: "Government & NSDL",
      icon: <SafetyOutlined />,
      color: "#eb2f96",
      description: "Government securities and regulatory data",
      operations: [
        {
          name: "Government Schemes",
          key: "government-schemes",
          status: "idle",
          lastRun: "3 hours ago",
          successRate: 98,
        },
        {
          name: "NSDL Inventories",
          key: "nsdl-inventories",
          status: "idle",
          lastRun: "2 hours ago",
          successRate: 97,
        },
        {
          name: "Master ISIN Data",
          key: "master",
          status: "idle",
          lastRun: "1 hour ago",
          successRate: 99,
        },
        {
          name: "Master Issuer Data",
          key: "master-issuer",
          status: "idle",
          lastRun: "1 hour ago",
          successRate: 99,
        },
      ],
    },
    {
      category: "SCAN Data",
      icon: <TrophyOutlined />,
      color: "#13c2c2",
      description: "Advanced analytics and scoring",
      operations: [
        {
          name: "Financial Strength",
          key: "financial-strength",
          status: "idle",
          lastRun: "4 hours ago",
          successRate: 96,
        },
        {
          name: "Governance Data",
          key: "governance",
          status: "idle",
          lastRun: "5 hours ago",
          successRate: 95,
        },
        {
          name: "Complexity Data",
          key: "complexity",
          status: "idle",
          lastRun: "3 hours ago",
          successRate: 97,
        },
        {
          name: "Market Activity",
          key: "market-activity",
          status: "idle",
          lastRun: "2 hours ago",
          successRate: 98,
        },
      ],
    },
  ]);

  // Mock chart data
  const [chartData, setChartData] = useState({
    operationsTrend: [
      { date: "2024-01-10", completed: 25, failed: 2, pending: 5 },
      { date: "2024-01-11", completed: 28, failed: 1, pending: 3 },
      { date: "2024-01-12", completed: 30, failed: 0, pending: 2 },
      { date: "2024-01-13", completed: 27, failed: 3, pending: 4 },
      { date: "2024-01-14", completed: 32, failed: 1, pending: 1 },
      { date: "2024-01-15", completed: 28, failed: 1, pending: 3 },
    ],
    userActivity: [
      { hour: "00:00", users: 45 },
      { hour: "04:00", users: 23 },
      { hour: "08:00", users: 156 },
      { hour: "12:00", users: 234 },
      { hour: "16:00", users: 189 },
      { hour: "20:00", users: 98 },
    ],
  });

  useEffect(() => {
    fetchSystemStats();
    fetchRecentActivity();
    fetchPerformanceMetrics();
    fetchUserStats();
    check2FAStatus();
  }, []);

  // Debug environment variables
  console.log("AdminDashboard - Environment variables:", {
    REACT_APP_AUTH_URL: process.env.REACT_APP_AUTH_URL,
    NODE_ENV: process.env.NODE_ENV,
  });



  const fetchSystemStats = async () => {
    try {
      // Enhanced stats with more realistic data
      setStats({
        totalOperations: 32,
        completedOperations: 28,
        pendingOperations: 3,
        failedOperations: 1,
        totalUsers: 1247,
        activeUsers: 892,
        totalRevenue: 45678,
        monthlyGrowth: 12.5,
        systemUptime: 99.8,
        avgResponseTime: 245,
        dataProcessed: 2.4,
        alertsCount: 3,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      setRecentActivity([
        {
          id: 1,
          operation: "Care Rating Fetch",
          status: "completed",
          timestamp: "2 minutes ago",
          user: "System",
          details: "Successfully fetched 1,247 rating records",
          icon: <CheckCircleOutlined />,
        },
        {
          id: 2,
          operation: "NSE Data Sync",
          status: "completed",
          timestamp: "5 minutes ago",
          user: "System",
          details: "Synchronized 3,456 ISIN records",
          icon: <SyncOutlined />,
        },
        {
          id: 3,
          operation: "News Approval",
          status: "pending",
          timestamp: "10 minutes ago",
          user: "admin@example.com",
          details: "Pending approval for 5 news articles",
          icon: <ClockCircleOutlined />,
        },
        {
          id: 4,
          operation: "Financial Data Update",
          status: "failed",
          timestamp: "15 minutes ago",
          user: "System",
          details: "Connection timeout to external API",
          icon: <ExclamationCircleOutlined />,
        },
        {
          id: 5,
          operation: "User Registration",
          status: "completed",
          timestamp: "20 minutes ago",
          user: "john.doe@example.com",
          details: "New user account created successfully",
          icon: <UserOutlined />,
        },
        {
          id: 6,
          operation: "System Backup",
          status: "completed",
          timestamp: "1 hour ago",
          user: "System",
          details: "Daily backup completed (2.4 GB)",
          icon: <CloudOutlined />,
        },
      ]);
    } catch (error) {
      console.error("Error fetching activity:", error);
    }
  };

  const fetchPerformanceMetrics = async () => {
    try {
      setPerformanceMetrics({
        cpuUsage: 45,
        memoryUsage: 62,
        diskUsage: 78,
        networkUsage: 23,
      });
    } catch (error) {
      console.error("Error fetching performance metrics:", error);
    }
  };

  const fetchUserStats = async () => {
    setUserStatsLoading(true);
    try {
      const authUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.get(`${authUrl}/admin-action/stats`);

      // Check if response has the expected structure with success field
      if (!response.data.success) {
        throw new Error("Failed to fetch user stats - success field is false");
      }

      const statsData = response.data.data;
      setUserStats(statsData);

      // Update the existing state variables for backward compatibility
      setDeactivatedUsersCount(statsData.deactivatedUsers);
      setDeletedUsersCount(statsData.deletedUsers);
    } catch (err) {
      console.error("Error fetching user stats:", err);
      // Set default values on error
      setUserStats({
        pendingActionUsers: 0,
        deactivatedUsers: 0,
        totalUsers: 0,
        activeUsers: 0,
        rejectedActions: 0,
        deletedUsers: 0
      });
      setDeactivatedUsersCount(0);
      setDeletedUsersCount(0);
    } finally {
      setUserStatsLoading(false);
    }
  };

  const executeOperation = async (operation) => {
    setLoading(true);
    try {
      updateOperationStatus(operation.key, "running");
      await performOperation(operation.key);
      updateOperationStatus(operation.key, "completed");
      message.success(`${operation.name} completed successfully`);
      fetchSystemStats();
      fetchRecentActivity();
    } catch (error) {
      console.error("Error executing operation:", error);
      updateOperationStatus(operation.key, "failed");
      message.error(`${operation.name} failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateOperationStatus = (key, status) => {
    setDataOperations((prev) =>
      prev.map((category) => ({
        ...category,
        operations: category.operations.map((op) =>
          op.key === key ? { ...op, status } : op
        ),
      }))
    );
  };

  const performOperation = async (key) => {
    const operationMap = {
      care: () => apiClient.get(`${baseUrl}/ratings/care`),
      crisil: () => apiClient.get(`${baseUrl}/ratings/crisil`),
      fitch: () => apiClient.get(`${baseUrl}/ratings/fitch`),
      icra: () => apiClient.get(`${baseUrl}/ratings/icra`),
      india: () => apiClient.get(`${baseUrl}/ratings/india`),
      news_et: () => apiClient.get(`${baseUrl}/news/et`),
      news_bs: () => apiClient.get(`${baseUrl}/news/bs`),
      news_fe: () => apiClient.get(`${baseUrl}/news/fe`),
      news_lm: () => apiClient.get(`${baseUrl}/news/lm`),
      news_mc: () => apiClient.get(`${baseUrl}/news/mc`),
    };

    const operation = operationMap[key];
    if (operation) {
      await operation();
    } else {
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  };

  // 2FA Functions
  const check2FAStatus = async () => {
    try {
      const baseUrl =
        process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";

      const response = await apiClient.get(`${baseUrl}/2fa/status`);

      console.log("2FA Status:", response.data);

      // Handle new API response structure with nested data
      const statusData = response.data.success && response.data.data ? response.data.data : response.data;

      // Check if methods array is empty - only show popup if no methods are available
      if (statusData.methods && statusData.methods.length === 0) {
        // Check if popup has already been shown for this session
        const popupShown = localStorage.getItem("2faPopupShown");
        if (!popupShown) {
          setShow2FAPopup(true);
          // Get user email from localStorage or token
          const userEmail = localStorage.getItem("userEmail") || "";
          setUserEmail(userEmail);

          // Fetch 2FA methods
          await fetchTwoFAMethods();

          // Mark popup as shown
          localStorage.setItem("2faPopupShown", "true");
        }
      } else {
        setShow2FAPopup(false);
      }
    } catch (error) {
      console.error("Error checking 2FA status:", error);
      console.error("2FA Status Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
      });
    }
  };

  const fetchTwoFAMethods = async () => {
    try {
      const baseUrl =
        process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";

      const response = await apiClient.get(`${baseUrl}/2fa/methods`);

      console.log("2FA Methods response:", response.data);

      // Handle new API response structure with nested data
      if (response.data.success && response.data.data) {
        setTwoFAMethods(response.data.data);
      } else {
        setTwoFAMethods(response.data); // Fallback
      }
    } catch (error) {
      console.error("Error fetching 2FA methods:", error);
      console.error("2FA Methods Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
      });
    }
  };

  const handleSkip2FA = async () => {
    message.success("2FA setup skipped");
    setShow2FAPopup(false);
    setTwoFAHandled(true);
    localStorage.setItem("twoFAHandled", "true");
    localStorage.setItem("2faPopupShown", "true");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "running":
        return <SyncOutlined spin style={{ color: "#1890ff" }} />;
      case "failed":
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
      case "pending":
        return <ClockCircleOutlined style={{ color: "#faad14" }} />;
      default:
        return <ClockCircleOutlined style={{ color: "#d9d9d9" }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "running":
        return "processing";
      case "failed":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const successRate = Math.round(
    (stats.completedOperations / stats.totalOperations) * 100
  );

  // Quick stats cards data
  const quickStats = [
    {
      title: "Total Users",
      value: userStats.totalUsers,
      prefix: <UserOutlined />,
      color: "#1890ff",
      suffix: "+12%",
      trend: "up",
    },
    {
      title: "Active Users",
      value: userStats.activeUsers,
      prefix: <TeamOutlined />,
      color: "#52c41a",
      suffix: "+8%",
      trend: "up",
    },
    {
      title: "Deactivated Users",
      value: userStats.deactivatedUsers,
      prefix: <UserOutlined />,
      color: "#faad14",
      suffix: "-5%",
      trend: "stable",
    },
    {
      title: "Deleted Users",
      value: userStats.deletedUsers,
      prefix: <DeleteOutlined />,
      color: "#ff4d4f",
      suffix: "-3%",
      trend: "down",
    }
  ];

  return (
    <div className={`admin-dashboard-container ${isDark ? 'dark' : ''}`}>
      {/* Hero Section */}
      <div
        style={{
          background: 'linear-gradient(90deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
          borderRadius: 20,
          padding: '32px',
          marginBottom: 32,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Overlay for better text readability in dark mode */}
        {isDark && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)',
              borderRadius: 20,
            }}
          />
        )}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          position: 'relative',
          zIndex: 1
        }}>
          <div>
            <Title
              level={1}
              style={{
                margin: 0,
                marginBottom: 12,
                fontWeight: 800,
                fontSize: 42,
                color: isDark ? "#ffffff" : "#1a1a1a",
              }}
            >
              Admin Dashboard
            </Title>
            <Text 
              style={{ 
                fontSize: 20, 
                color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                fontWeight: 400,
                lineHeight: 1.4,
              }}
            >
              Welcome back! Here's what's happening with your system today.
            </Text>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Select
                value={timeRange}
                onChange={setTimeRange}
                style={{ 
                  width: 140,
                  borderRadius: 8,
                  backgroundColor: isDark ? '#2a2a3e' : '#fff',
                  border: isDark ? '1px solid #3a3a4e' : '1px solid #d9d9d9',
                  color: isDark ? '#ffffff' : '#000000'
                }}
              >
                <Option value="1d">Last 24h</Option>
                <Option value="7d">Last 7 days</Option>
                <Option value="30d">Last 30 days</Option>
                <Option value="90d">Last 90 days</Option>
              </Select>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchSystemStats();
                  fetchRecentActivity();
                  fetchPerformanceMetrics();
                  fetchUserStats();
                }}
                style={{
                  borderRadius: 8,
                  height: 40,
                  fontWeight: 500,
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff'
                }}
              >
                Refresh
              </Button>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <DatePicker
                placeholder="Start Date"
                style={{ 
                  width: 140,
                  borderRadius: 8,
                  backgroundColor: isDark ? '#2a2a3e' : '#fff',
                  border: isDark ? '1px solid #3a3a4e' : '1px solid #d9d9d9'
                }}
              />
              <DatePicker
                placeholder="End Date"
                style={{ 
                  width: 140,
                  borderRadius: 8,
                  backgroundColor: isDark ? '#2a2a3e' : '#fff',
                  border: isDark ? '1px solid #3a3a4e' : '1px solid #d9d9d9'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {quickStats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
                borderRadius: 12,
                boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
                padding: "20px",
                position: "relative",
                height: 200,
                overflow: "hidden"
              }}
            >
              <div style={{ 
                position: "absolute",
                top: "16px",
                left: "16px",
                fontSize: 19,
                fontWeight: 600,
                fontStyle: "Semi Bold",
                color: isDark ? "#ffffff" : "#000",
                zIndex: 2
              }}>
                {stat.title}
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "20px",
                left: "16px",
                fontSize: 36, 
                fontWeight: 700, 
                fontStyle: "bold",
                color: isDark ? "#ffffff" : "#000",
                lineHeight: 1,
                zIndex: 1
              }}>
                {stat.value}
              </div>
              <div style={{ 
                position: "absolute",
                bottom: "16px",
                right: "16px",
                fontSize: 12, 
                color: stat.color,
                fontWeight: 500
              }}>
                {stat.suffix}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Main Dashboard Content */}
      <div className="dashboard-main-content" style={{ marginTop: 32 }}>
        <Row gutter={[24, 24]}>
          {/* Left Column - Charts and Analytics */}
          <Col xs={24} lg={16}>
            {/* System Overview Card */}
            <div
              style={{
                background: isDark ? "#2a2a3e" : "#fff",
                borderRadius: 12,
                boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.1)",
                padding: "24px",
                marginBottom: 24,
                border: isDark ? "1px solid #3a3a4e" : "none"
              }}
            >
              {/* Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 24
              }}>
                <Title level={4} style={{ 
                  margin: 0, 
                  fontSize: 20, 
                  fontWeight: 600,
                  color: isDark ? "#ffffff" : "#000"
                }}>
                  System Overview
                </Title>
                <div style={{
                  background: isDark ? "#1a3a2a" : "#f6ffed",
                  border: isDark ? "1px solid #4a7c59" : "1px solid #b7eb8f",
                  borderRadius: 6,
                  padding: "4px 12px",
                  fontSize: 14,
                  color: isDark ? "#ffffff" : "#000"
                }}>
                  Status: <span style={{ color: "#52c41a", fontWeight: 500 }}>Healthy</span>
                </div>
              </div>

              {/* Usage Gauges */}
              <Row gutter={[24, 24]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={45}
                      size={120}
                      strokeColor="#52c41a"
                      trailColor="#f0f0f0"
                      format={() => (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 600, color: isDark ? "#ffffff" : "#000", marginBottom: 4 }}>
                            45%
                          </div>
                          <div style={{ fontSize: 12, color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666", fontWeight: 400 }}>
                            CPU Usage
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={45}
                      size={120}
                      strokeColor="#fa8c16"
                      trailColor="#f0f0f0"
                      format={() => (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 600, color: isDark ? "#ffffff" : "#000", marginBottom: 4 }}>
                            45%
                          </div>
                          <div style={{ fontSize: 12, color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666", fontWeight: 400 }}>
                            Memory Usage
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={45}
                      size={120}
                      strokeColor="#1890ff"
                      trailColor="#f0f0f0"
                      format={() => (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 600, color: isDark ? "#ffffff" : "#000", marginBottom: 4 }}>
                            45%
                          </div>
                          <div style={{ fontSize: 12, color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666", fontWeight: 400 }}>
                            Disk Usage
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={45}
                      size={120}
                      strokeColor="#722ed1"
                      trailColor="#f0f0f0"
                      format={() => (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 600, color: isDark ? "#ffffff" : "#000", marginBottom: 4 }}>
                            45%
                          </div>
                          <div style={{ fontSize: 12, color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666", fontWeight: 400 }}>
                            Network
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </Col>
              </Row>

              {/* Overall System Progress */}
              <div style={{ marginTop: 24 }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: 16
                }}>
                  <Text style={{ 
                    fontSize: 16, 
                    fontWeight: 600, 
                    color: isDark ? "#ffffff" : "#000"
                  }}>
                    Overall System Progress
                  </Text>
                  <Text style={{ 
                    fontSize: 14, 
                    color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666"
                  }}>
                    Success rate: <span style={{ fontWeight: 600, color: isDark ? "#ffffff" : "#000" }}>88%</span>
                  </Text>
                </div>
                <Progress
                  percent={88}
                  strokeColor="#52c41a"
                  trailColor="#f0f0f0"
                  showInfo={false}
                  style={{ marginBottom: 16 }}
                />
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  gap: 24,
                  fontSize: 14,
                  color: isDark ? "#ffffff" : "#000"
                }}>
                  <span>Completed: 28</span>
                  <span>Pending: 3</span>
                  <span style={{ color: "#ff4d4f" }}>Failed: 1</span>
                </div>
              </div>
            </div>

            {/* Data Operations */}
            <Card className="operations-card">
              <div className="card-header">
                <Title level={4} className="card-title">
                  <DatabaseOutlined /> Data Operations
                </Title>
                <Button type="primary" size="small" icon={<RocketOutlined />}>
                  Run All Operations
                </Button>
              </div>

               {/* Custom Tabs */}
               <div style={{ display: 'flex', alignItems: 'center', gap: 0, backgroundColor: isDark ? '#3a3a4e' : '#f5f5f5', borderRadius: 12, padding: 4, marginBottom: 24 }}>
                 {dataOperations.map((category, index) => (
                   <div
                     key={index}
                     onClick={() => setActiveTab(index)}
                     style={{
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'center',
                       padding: '12px 16px',
                       borderRadius: 8,
                       cursor: 'pointer',
                       backgroundColor: activeTab === index ? (isDark ? '#8b5cf6' : '#9FF5F8') : 'transparent',
                       transition: 'all 0.2s ease',
                       width: 200
                     }}
                   >
                     <span style={{ color: category.color, fontSize: 20, marginBottom: 4, fontWeight: 'bold', textAlign: 'center' }}>
                       {category.icon}
                     </span>
                     <span style={{ fontSize: 15, fontWeight: 700, fontStyle: 'bold', color: isDark ? '#ffffff' : '#333', textAlign: 'center' }}>
                       {category.category}
                     </span>
                   </div>
                 ))}
               </div>

               {/* Tab Content */}
               {dataOperations.map((category, index) => (
                 activeTab === index && (
                   <div key={index}>
                     <div className="category-description">
                       <Text type="secondary">{category.description}</Text>
                     </div>
                     <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                       {category.operations.map((operation, opIndex) => (
                         <div
                           key={opIndex}
                           style={{
                             background: isDark ? "#2a2a3e" : "#fff",
                             borderRadius: 12,
                             boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.1)",
                             padding: "20px",
                             position: "relative",
                             minHeight: "120px",
                             border: isDark ? "1px solid #3a3a4e" : "none"
                           }}
                         >
                           {/* Header Section - Top Left */}
                           <div style={{ 
                             display: 'flex', 
                             alignItems: 'center', 
                             gap: '12px',
                             marginBottom: '16px'
                           }}>
                             <div style={{ 
                               fontSize: 24, 
                               fontWeight: 600, 
                               color: isDark ? "#ffffff" : "#000"
                             }}>
                               {operation.name}
                             </div>
                             <div style={{
                               background: isDark ? "#1a3a2a" : "#f6ffed",
                               border: isDark ? "1px solid #4a7c59" : "1px solid #b7eb8f",
                               borderRadius: 6,
                               padding: "2px 8px",
                               fontSize: 12,
                               color: "#52c41a",
                               fontWeight: 500
                             }}>
                               {operation.successRate}% Success
                             </div>
                           </div>

                           {/* Main Content Area - Empty white space */}
                           <div style={{ 
                             height: "40px",
                             marginBottom: "16px"
                           }}></div>

                           {/* Footer Section */}
                           <div style={{ 
                             display: 'flex', 
                             justifyContent: 'space-between', 
                             alignItems: 'center',
                             position: 'absolute',
                             bottom: '20px',
                             left: '20px',
                             right: '20px'
                           }}>
                             <Text style={{ 
                               fontSize: 12, 
                               color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666"
                             }}>
                               Last run: {operation.lastRun}
                             </Text>
                             <Button
                               type="primary"
                               size="small"
                               onClick={() => executeOperation(operation)}
                               disabled={
                                 loading || operation.status === "running"
                               }
                               loading={operation.status === "running"}
                               style={{
                                 borderRadius: 6,
                                 fontWeight: 500,
                                 backgroundColor: '#1890ff',
                                 borderColor: '#1890ff'
                               }}
                             >
                               {operation.status === "running"
                                 ? "Running"
                                 : "Execute"}
                             </Button>
                           </div>
                         </div>
                       ))}
                     </div>
                   </div>
                 )
               ))}
            </Card>
          </Col>

          {/* Right Column - Activity and Quick Actions */}
          <Col xs={24} lg={8}>
            {/* Recent Activity */}
            <Card className="activity-card">
              <div className="card-header">
                <Title level={4} className="card-title">
                  <BellOutlined /> Recent Activity
                </Title>
                <Button type="text" size="small">
                  View All
                </Button>
              </div>

              <Timeline className="activity-timeline">
                {recentActivity.map((activity) => (
                  <Timeline.Item
                    key={activity.id}
                    color={getStatusColor(activity.status)}
                    dot={activity.icon}
                  >
                    <div className="timeline-content">
                      <div className="timeline-title">{activity.operation}</div>
                      <div className="timeline-details">{activity.details}</div>
                      <div className="timeline-meta">
                        <Text type="secondary">{activity.timestamp}</Text>
                        <Text type="secondary">• {activity.user}</Text>
                      </div>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          </Col>
        </Row>
      </div>

      {/* 2FA Popup */}
      <TwoFactorAuthPopup
        visible={show2FAPopup}
        onClose={() => {
          setShow2FAPopup(false);
          setTwoFAHandled(true);
          localStorage.setItem("twoFAHandled", "true");
        }}
        email={userEmail}
        onSkip={handleSkip2FA}
      />
    </div>
  );
};

export default AdminDashboard;
